import { DASHBOARD_GROUPS } from '../../../Constants/customDashboards';
import { MANAGEMENT_MODULES, FEATURE_MODULES, EXPERIMENTAL_MODULES } from '../constants';

export const extractAndCleanPathFromContent = (content) =>
    content.path.split('?')[0].trim().replace(/^\/+|\/+$/g, '');

export const normalizeModulePath = (path = '') =>
    path.toString().trim().toLowerCase().replace(/^\/+|\/+$/g, '').replace(/[?#].*$/, '');

export const buildUniquePathList = (paths) =>
    paths.reduce((acc, val) => (acc.includes(val) ? acc : [...acc, val]), []);

// Keep one source of truth for module path catalogs used by ModulePermission screens.
export const dashboardPaths = buildUniquePathList(
    DASHBOARD_GROUPS
        .flatMap(dg => dg.contents.filter(c => !!c.path).map(extractAndCleanPathFromContent)),
);

export const adminPaths = buildUniquePathList(
    Object.values(MANAGEMENT_MODULES)
        .filter(Boolean)
        .map(extractAndCleanPathFromContent),
);

export const featurePaths = buildUniquePathList(
    FEATURE_MODULES
        .filter(Boolean)
        .map(extractAndCleanPathFromContent),
);

export const experimentalPaths = buildUniquePathList(
    EXPERIMENTAL_MODULES
        .filter(Boolean)
        .map(extractAndCleanPathFromContent),
);

const slugify = (value = '') => value.toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const escapeRegExp = (text = '') => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const createRoutePatternRegex = (pathForRoute = '') => {
    const normalized = normalizeModulePath(pathForRoute);
    if (!normalized) return null;

    const regexBody = normalized
        .split('/')
        .map(segment => (segment.startsWith(':') ? '[^/]+' : escapeRegExp(segment)))
        .join('/');

    return new RegExp(`^${regexBody}$`, 'i');
};

// Dashboard grouping basis comes from customDashboards.js sections.
export const dashboardGroupCatalog = DASHBOARD_GROUPS
    .map((groupConfig) => {
        const groupTitle = groupConfig?.title || 'Dashboards';
        const groupKey = `dashboard:${slugify(groupTitle)}`;
        const contents = Array.isArray(groupConfig?.contents) ? groupConfig.contents : [];

        const exactPaths = buildUniquePathList(
            contents
                .filter(content => !!content?.path)
                .map(content => normalizeModulePath(content.path)),
        );

        const patternRegexes = contents
            .filter(content => !!content?.pathForRoute)
            .map(content => createRoutePatternRegex(content.pathForRoute))
            .filter(Boolean);

        return {
            groupKey,
            groupLabel: groupTitle,
            exactPaths,
            patternRegexes,
        };
    })
    .filter(group => group.exactPaths.length > 0 || group.patternRegexes.length > 0);

export const dashboardGroupLabelsByKey = dashboardGroupCatalog.reduce((acc, group) => {
    acc[group.groupKey] = group.groupLabel;
    return acc;
}, {});

export const resolveDashboardGroupByPath = (modulePath) => {
    const normalized = normalizeModulePath(modulePath);
    if (!normalized) return null;

    const exactMatch = dashboardGroupCatalog.find(group => group.exactPaths.includes(normalized));
    if (exactMatch) return { key: exactMatch.groupKey, label: exactMatch.groupLabel };

    const patternMatch = dashboardGroupCatalog.find(group =>
        group.patternRegexes.some(regex => regex.test(normalized)),
    );
    if (patternMatch) return { key: patternMatch.groupKey, label: patternMatch.groupLabel };

    return null;
};

